import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveImage } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n/translations";
import { calcMonthlyRate, fmtEUR } from "@/lib/finance";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import ContactSection from "@/components/sections/ContactSection";
import FinanceCalculator from "@/components/FinanceCalculator";
import { ArrowLeft, Calendar, Gauge, Zap, Fuel, Cog, Car, MessageCircle, RefreshCw, Heart, Check } from "lucide-react";

const fmtPrice = (p) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(p);
const fmtNum = (n) => new Intl.NumberFormat("de-DE").format(n);

// Formatiert mobile.de-Beschreibungen: Überschriften ("Sonderausstattung:"),
// Komma-Listen als Ausstattungs-Aufzählung, sonst Absätze
function DescriptionBlock({ text }) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const blocks = [];
  lines.forEach((line) => {
    if (line.endsWith(":") && line.length < 80) {
      blocks.push({ type: "heading", text: line.replace(/:$/, "") });
    } else if ((line.match(/,/g) || []).length >= 3) {
      blocks.push({ type: "list", items: line.split(/,(?!\d)/).map((i) => i.trim()).filter(Boolean) });
    } else {
      blocks.push({ type: "paragraph", text: line });
    }
  });
  return (
    <div className="space-y-6" data-testid="detail-description-content">
      {blocks.map((b, i) => {
        if (b.type === "heading") {
          return (
            <h4 key={i} className="font-heading font-extrabold uppercase tracking-tight text-white text-base md:text-lg pt-2 border-l-2 border-brand-light pl-3">
              {b.text}
            </h4>
          );
        }
        if (b.type === "list") {
          return (
            <ul key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {b.items.map((item, j) => (
                <li key={j} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-snug">
                  <Check className="w-4 h-4 text-brand-bright shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="text-zinc-300 leading-relaxed text-sm md:text-base">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/vehicles/${id}`)
      .then(({ data }) => setVehicle(data))
      .catch(() => setNotFound(true));
  }, [id]);

  // SEO: dynamic title/description + Vehicle JSON-LD
  useEffect(() => {
    if (!vehicle) return;
    const title = `${vehicle.brand} ${vehicle.model} · ${vehicle.year} · ${fmtNum(vehicle.mileage)} km | DNA Automotive Köln`;
    const desc = `${vehicle.brand} ${vehicle.model} zum Verkauf bei DNA Automotive in der MotorWorld Köln. ${vehicle.year}, ${fmtNum(vehicle.mileage)} km, ${vehicle.power_hp} PS, ${vehicle.fuel}. Finanzierung ab 4,9 % über Santander.`;
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", desc);
    const fin = calcMonthlyRate(vehicle.price, {
      down: vehicle.finance_down, term: vehicle.finance_term,
      balloon: vehicle.finance_balloon, annualRate: vehicle.finance_rate,
    });
    const ld = {
      "@context": "https://schema.org",
      "@type": "Vehicle",
      name: `${vehicle.brand} ${vehicle.model}`,
      brand: { "@type": "Brand", name: vehicle.brand },
      model: vehicle.model,
      vehicleModelDate: vehicle.year,
      mileageFromOdometer: { "@type": "QuantitativeValue", value: vehicle.mileage, unitCode: "KMT" },
      fuelType: vehicle.fuel,
      vehicleTransmission: vehicle.transmission,
      bodyType: vehicle.body_type,
      offers: {
        "@type": "Offer",
        price: vehicle.price,
        priceCurrency: "EUR",
        availability: vehicle.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
        seller: {
          "@type": "AutoDealer",
          name: "DNA Automotive GmbH",
          address: { "@type": "PostalAddress", streetAddress: "Butzweilerstraße 35-39", addressLocality: "Köln", postalCode: "50829", addressCountry: "DE" },
        },
      },
      image: (vehicle.images || []).map((im) => resolveImage(im)),
      description: `${vehicle.brand} ${vehicle.model}, ${vehicle.year}, ${vehicle.power_hp} PS, ${vehicle.fuel}. Finanzierung ab ${fmtEUR(fin.rate)}/Monat.`,
    };
    let s = document.getElementById("vehicle-jsonld");
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "vehicle-jsonld"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
    return () => { s && s.remove(); };
  }, [vehicle]);

  if (notFound) {
    return (
      <div className="bg-obsidian min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
          <p className="text-zinc-300 text-xl">Fahrzeug nicht gefunden.</p>
          <Link to="/" className="bg-brand text-black px-6 py-3 font-bold uppercase tracking-wider">{t.detail.back}</Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!vehicle) {
    return <div className="bg-obsidian min-h-screen flex items-center justify-center text-brand-bright uppercase tracking-widest">Laden...</div>;
  }

  const desc = vehicle[`description_${lang}`] || vehicle.description_de;
  const images = vehicle.images?.length ? vehicle.images : ["/vehicle-placeholder.jpg"];
  const specs = [
    { icon: Calendar, label: t.specs.year, value: vehicle.year },
    { icon: Gauge, label: t.specs.mileage, value: `${fmtNum(vehicle.mileage)} km` },
    { icon: Zap, label: t.specs.power, value: `${vehicle.power_hp} PS` },
    { icon: Fuel, label: t.specs.fuel, value: vehicle.fuel },
    { icon: Cog, label: t.specs.transmission, value: vehicle.transmission },
    { icon: Car, label: t.specs.body, value: vehicle.body_type },
  ];
  const waText = `Hallo DNA Automotive, ich interessiere mich für den ${vehicle.brand} ${vehicle.model} (${fmtPrice(vehicle.price)}).`;
  const fin = calcMonthlyRate(vehicle.price, {
    down: vehicle.finance_down, term: vehicle.finance_term,
    balloon: vehicle.finance_balloon, annualRate: vehicle.finance_rate,
  });

  return (
    <div className="bg-obsidian min-h-screen" data-testid="vehicle-detail-page">
      <Header />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-32 pb-20">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-zinc-400 hover:text-brand-bright transition-colors mb-10 uppercase tracking-wider text-sm" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> {t.detail.back}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative aspect-[16/11] overflow-hidden border border-white/10 bg-obsidian-surface">
              <img src={resolveImage(images[activeImg])} alt={`${vehicle.brand} ${vehicle.model}`} className="w-full h-full object-cover" />
              {vehicle.status === "sold" && (
                <span className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5">{t.fuhrpark.sold}</span>
              )}
            </motion.div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-4 flex-wrap">
                {images.map((im, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`w-24 h-16 overflow-hidden border ${i === activeImg ? "border-brand-light" : "border-white/10"}`} data-testid={`thumb-${i}`}>
                    <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-brand-bright text-xs uppercase tracking-[0.3em] mb-3">{vehicle.brand}</p>
            <h1 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95] mb-6" data-testid="detail-title">
              {vehicle.model}
            </h1>
            <div className="font-heading text-4xl font-extrabold text-brand-bright mb-1">{fmtPrice(vehicle.price)}</div>
            {vehicle.status !== "sold" && (
              <div className="text-sm text-zinc-300 mb-8">
                Finanzierung ab <span className="text-white font-bold">{fmtEUR(fin.rate)} / Monat</span>
                <span className="text-zinc-500">*</span>
                <span className="block text-[11px] text-zinc-500 mt-0.5">
                  {fin.annualRate.toFixed(2).replace(".", ",")} % über {fin.partner}
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
              {specs.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-obsidian p-5">
                    <Icon className="w-5 h-5 text-brand-bright mb-2" />
                    <div className="text-xs uppercase tracking-wider text-zinc-500">{s.label}</div>
                    <div className="text-white font-semibold mt-1">{s.value}</div>
                  </div>
                );
              })}
            </div>

            {desc && (
              <div className="mb-8">
                <h3 className="text-xs uppercase tracking-[0.25em] text-brand-bright mb-5">{t.detail.description}</h3>
                <DescriptionBlock text={desc} />
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(waText)}`} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp" className="inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-6 py-4 hover:bg-brand-hover transition-colors">
                <MessageCircle className="w-4 h-4" /> WhatsApp Beratung
              </a>
              <button onClick={() => document.getElementById("finanzierung")?.scrollIntoView({ behavior: "smooth" })} data-testid="detail-finance" className="inline-flex items-center gap-2 border border-white/25 text-white font-medium uppercase tracking-wider text-sm px-6 py-4 hover:border-brand-light hover:text-brand-bright transition-colors">
                Finanzierung
              </button>
              <Link to="/inzahlungnahme" data-testid="detail-tradein" className="inline-flex items-center gap-2 border border-white/25 text-white font-medium uppercase tracking-wider text-sm px-6 py-4 hover:border-brand-light hover:text-brand-bright transition-colors">
                <RefreshCw className="w-4 h-4" /> Fahrzeug in Zahlung geben
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Finance calculator (interactive) */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <FinanceCalculator vehicle={vehicle} />
      </div>

      <ContactSection prefillVehicle={`${vehicle.brand} ${vehicle.model}`} />
      <Footer />
      <WhatsAppButton prefill={waText} />
    </div>
  );
}
